// components/tracking/ShipmentDetails.jsx - Updated with correct Order Placed and Expected Delivery logic
import React from 'react';
import {
  FaReceipt, FaBarcode, FaMoneyBillWave, FaTruckMoving, 
  FaArrowUp, FaArrowDown, FaClock
} from 'react-icons/fa';

const DetailCard = ({ icon: Icon, label, value, iconColor = "text-blue-600", bgColor = "bg-blue-50" }) => (
  <div className="group relative bg-white rounded-xl p-4 border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 h-20 flex items-center">
    <div className="flex items-center gap-4 w-full">
      <div className={`p-3 rounded-xl ${bgColor} ${iconColor} shadow-sm group-hover:shadow-md transition-all duration-200 flex-shrink-0`}>
        <Icon className="text-lg" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">
          {label}
        </p>
        <p className="font-bold text-gray-900 text-base leading-tight break-words">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const ShipmentDetails = ({ data }) => {
  const details = [
    {
      icon: FaBarcode,
      label: "AWB Number",
      value: data.awb_number,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: FaReceipt,
      label: "Order Reference", 
      value: data.order_reference_number,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      icon: FaMoneyBillWave,
      label: "Payment Mode",
      value: data.payment_mode,
      iconColor: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: FaTruckMoving,
      label: "Carrier Partner",
      value: data.carrier_partner_public_name,
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      icon: FaArrowUp,
      label: "Pickup Location",
      value: `${data.pickup_details.pickup_city}, ${data.pickup_details.pickup_state}`,
      iconColor: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: FaArrowDown,
      label: "Delivery Location", 
      value: `${data.drop_details.drop_city}, ${data.drop_details.drop_state}`,
      iconColor: "text-red-600",
      bgColor: "bg-red-50"
    }
  ];

  // Get order placed date and time from order_status_date_time_ist where order_status_code = 30
  const getOrderPlacedDateTime = () => {
    if (data.order_processing_business_status_codes && Array.isArray(data.order_processing_business_status_codes)) {
      const orderPlacedStatus = data.order_processing_business_status_codes.find(
        status => status.order_status_code === 30 || status.order_status_code === "30"
      );
      if (orderPlacedStatus && orderPlacedStatus.order_status_date_time_ist) {
        try {
          return new Date(orderPlacedStatus.order_status_date_time_ist).toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } catch (error) {
          console.error('Error formatting order placed date time:', error);
          return orderPlacedStatus.order_status_date_time_ist;
        }
      }
    }
    return "Not Available";
  };

  // Get expected delivery date and time from carrier_partner_edd of the latest order_processing_business_status_code
  const getExpectedDeliveryDateTime = () => {
    if (data.order_processing_business_status_codes && Array.isArray(data.order_processing_business_status_codes) && data.order_processing_business_status_codes.length > 0) {
      // Sort by date to get the latest status (most recent first)
      const sortedStatuses = [...data.order_processing_business_status_codes].sort(
        (a, b) => new Date(b.order_status_date_time_ist) - new Date(a.order_status_date_time_ist)
      );
      
      // Get the latest status
      const latestStatus = sortedStatuses[0];
      
      if (latestStatus && latestStatus.carrier_partner_edd) {
        try {
          return new Date(latestStatus.carrier_partner_edd).toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
        } catch (error) {
          console.error('Error formatting expected delivery date time:', error);
          return latestStatus.carrier_partner_edd;
        }
      }
    }
    return "Not Available";
  };

  return (
    <div className="bg-white rounded-l-2xl 2xl:rounded-r-none rounded-r-2xl border border-gray-200 2xl:border-r-0 p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 pb-5 border-b border-gray-100">
        <div className="p-3 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg">
          <FaReceipt className="text-white text-xl" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Shipment Details</h2>
          <p className="text-sm text-gray-500">Complete order information</p>
        </div>
      </div>
      
      {/* Details Grid - Now uses full 2x3 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
        {details.map((detail, index) => (
          <DetailCard
            key={index}
            icon={detail.icon}
            label={detail.label}
            value={detail.value}
            iconColor={detail.iconColor}
            bgColor={detail.bgColor}
          />
        ))}
      </div>

      {/* Enhanced Quick Stats Footer with Dates Only */}
      <div className="mt-6 pt-5 border-t border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Order Placed Date */}
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <FaClock className="text-white text-sm" />
              </div>
              <p className="text-xs text-indigo-700 font-semibold uppercase tracking-wide">Order Placed</p>
            </div>
            <p className="font-bold text-indigo-900 text-base leading-tight">
              {getOrderPlacedDateTime()}
            </p>
          </div>

          {/* Expected Delivery Date */}
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200 hover:border-emerald-300 hover:shadow-md transition-all duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-600 rounded-lg">
                <FaTruckMoving className="text-white text-sm" />
              </div>
              <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Expected Delivery</p>
            </div>
            <p className="font-bold text-emerald-900 text-base leading-tight">
              {getExpectedDeliveryDateTime()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetails;